import React, { useState } from 'react';
import { Box, Typography, IconButton, Button, Menu, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft as ChevronLeftIcon,
  MoreHoriz as MoreHorizIcon,
  KeyboardArrowDown as ArrowDownIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';

interface Transaction {
  id: string;
  date: string;
  time: string;
  description: string;
  amount: string;
  type: 'payment' | 'refund' | 'withdrawal' | 'fee';
  isPending?: boolean;
}

// Mock transaction data
const mockTransactions: Transaction[] = [
  {
    id: '1',
    date: '20 Aug',
    time: '23:04',
    description: 'Payment for order #10272',
    amount: '$840.00',
    type: 'payment',
    isPending: true,
  },
  {
    id: '2',
    date: '18 Aug',
    time: '14:32',
    description: 'Partial refund for order #28425',
    amount: '-$120.00',
    type: 'refund',
  },
  {
    id: '3',
    date: '17 Aug',
    time: '18:48',
    description: 'Full refund for order #12403',
    amount: '-$1,040.00',
    type: 'refund',
  },
  {
    id: '4',
    date: '5 Aug',
    time: '01:14',
    description: 'Withdrawal to Dukhan Bank •4629',
    amount: '-$9,140.00',
    type: 'withdrawal',
  },
  {
    id: '5',
    date: '24 Jul',
    time: '15:24',
    description: 'Eventi fee for order #10017',
    amount: '-$111.00',
    type: 'fee',
  },
  {
    id: '6',
    date: '31 Jul',
    time: '22:19',
    description: 'Payment for order #10017',
    amount: '$1,110.00',
    type: 'payment',
  },
  {
    id: '7',
    date: '24 Jul',
    time: '15:24',
    description: 'Eventi fee for order #39264',
    amount: '-$65.00',
    type: 'fee',
  },
  {
    id: '8',
    date: '30 Jul',
    time: '14:35',
    description: 'Payment for order #39264',
    amount: '$650.00',
    type: 'payment',
  },
  {
    id: '9',
    date: '24 Jul',
    time: '15:24',
    description: 'Eventi fee for order #10386',
    amount: '-$98.00',
    type: 'fee',
  },
  {
    id: '10',
    date: '24 Jul',
    time: '15:24',
    description: 'Payment for order #10386',
    amount: '$980.00',
    type: 'payment',
  },
];

const VendorTransactions: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleBack = () => {
    navigate('/vendors');
  };

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: '24px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: '16px',
          width: '100%',
        }}
      >
        {/* Title Section with Back Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <IconButton
            onClick={handleBack}
            sx={{
              width: '32px',
              height: '32px',
              padding: 0,
              color: 'var(--palette-light-brand-primary-value-hex)',
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <Typography
            variant="h1"
            sx={{
              fontSize: {
                xs: '32px',
                sm: '36px',
                md: 'var(--palette-light-font-size-h1-value)',
              },
              fontWeight: 700,
              lineHeight: '1.15',
              letterSpacing: '-1%',
              color: 'var(--palette-light-text-primary-value-hex)',
            }}
          >
            Bob's Burgers
          </Typography>
        </Box>

        {/* Actions Menu */}
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: '56px',
            height: '56px',
            border: '1px solid var(--palette-light-brand-primary-value-hex)',
            borderRadius: '16px',
            color: 'var(--palette-light-brand-primary-value-hex)',
          }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => setAnchorEl(null)}>Edit Vendor</MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)}>View Profile</MenuItem>
        </Menu>
      </Box>

      {/* Tabs and Filter */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignSelf: 'stretch',
          flexWrap: 'wrap',
          gap: '8px',
          width: '100%',
        }}
      >
        {/* Tabs */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', flex: 1 }}>
          {['Profile', 'Services', 'Orders', 'Transactions'].map((tab) => (
            <Box
              key={tab}
              onClick={() => {
                if (tab === 'Profile') navigate(`/vendors/${id}`);
                else if (tab === 'Services') navigate(`/vendors/${id}/services`);
                else if (tab === 'Orders') navigate(`/vendors/${id}/orders`);
                else if (tab === 'Transactions') navigate(`/vendors/${id}/transactions`);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px 7px',
                height: '39px',
                backgroundColor:
                  tab === 'Transactions'
                    ? 'var(--palette-light-brand-secondary-value-hex)'
                    : 'var(--palette-light-background-secondary-value-hex)',
                borderRadius: '16px',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color:
                    tab === 'Transactions'
                      ? 'var(--palette-light-text-foreground-value-hex)'
                      : 'var(--palette-light-text-primary-value-hex)',
                }}
              >
                {tab}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Type Filter */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px 7px',
              height: '39px',
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: '16px',
              cursor: 'pointer',
            }}
          >
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
              Any type
            </Typography>
            <ArrowDownIcon sx={{ fontSize: '24px' }} />
          </Box>
        </Box>
      </Box>

      {/* Wallet Stats Card */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignSelf: 'stretch',
          gap: '8px',
          padding: '24px',
          backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
          borderRadius: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'stretch',
            alignSelf: 'stretch',
            gap: '8px',
          }}
        >
          {/* Total Earned */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
              $25,890.00
            </Typography>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
              Total earned with Eventi
            </Typography>
          </Box>

          {/* Available to Withdraw */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
              $5,240.00
            </Typography>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
              Available to withdraw
            </Typography>
          </Box>

          {/* Eventi Fee */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
              5%
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                Eventi fee
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-brand-primary-value-hex)' }}>
                Change
              </Typography>
            </Box>
          </Box>

          {/* Withdrawal Account */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, justifyContent: 'flex-end' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-brand-primary-value-hex)' }}>
              Withdrawal account...
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Transactions Table */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', width: '100%', boxSizing: 'border-box', overflowX: 'auto' }}>
        {/* Table Header - Desktop Only */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            alignSelf: 'stretch',
            gap: '16px',
            padding: '12px 0px',
            borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ width: '90px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.2', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Date/time
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.2', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Description
            </Typography>
          </Box>
          <Box sx={{ width: '120px', display: 'flex', justifyContent: 'flex-start' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.2', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Amount
            </Typography>
          </Box>
        </Box>

        {/* Table Rows */}
        {mockTransactions.map((transaction) => (
          <Box
            key={transaction.id}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'baseline' },
              alignSelf: 'stretch',
              gap: { xs: '8px', md: '16px' },
              padding: { xs: '16px 0px', md: '16px 0px' },
              borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {/* Date/Time */}
            <Box sx={{ width: { xs: '100%', md: '90px' }, display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {transaction.date}, {transaction.time}
              </Typography>
            </Box>

            {/* Description */}
            <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: { xs: '100%', md: 0 }, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {transaction.description}
              </Typography>
            </Box>

            {/* Amount */}
            <Box sx={{ width: { xs: '100%', md: '120px' }, display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 400,
                  lineHeight: '1.4',
                  color: transaction.amount.startsWith('-') ? 'var(--palette-light-status-danger-value-hex)' : 'var(--palette-light-text-primary-value-hex)',
                }}
              >
                {transaction.amount}
              </Typography>
              {transaction.isPending && (
                <ClockIcon sx={{ fontSize: '16px', color: 'var(--palette-light-text-secondary-value-hex)' }} />
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
          mt: '32px',
          flexShrink: 0,
          gap: { xs: '16px', sm: '16px', md: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            borderRadius: '16px',
            backgroundColor: '#FFFFFF',
            padding: '4px',
          }}
        >
          <Button
            sx={{
              backgroundColor: '#F4DBC5',
              borderRadius: '50%',
              minWidth: '56px',
              width: '56px',
              height: '56px',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              color: 'var(--palette-light-text-foreground-value-hex)',
              textTransform: 'none',
              padding: 0,
              '&:hover': {
                backgroundColor: '#F4DBC5',
              },
            }}
          >
            1
          </Button>
          <Button
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '6px',
              minWidth: '56px',
              width: '56px',
              height: '56px',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 400,
              color: 'var(--palette-light-text-primary-value-hex)',
              textTransform: 'none',
              padding: 0,
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-tertiary-value-hex)',
              },
            }}
          >
            2
          </Button>
          <Button
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '6px',
              minWidth: '56px',
              width: '56px',
              height: '56px',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 400,
              color: 'var(--palette-light-text-primary-value-hex)',
              textTransform: 'none',
              padding: 0,
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-tertiary-value-hex)',
              },
            }}
          >
            3
          </Button>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '56px',
              width: '56px',
              height: '56px',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 400,
              color: 'var(--palette-light-text-primary-value-hex)',
            }}
          >
            ...
          </Box>
          <Button
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '6px',
              minWidth: '56px',
              width: '56px',
              height: '56px',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 400,
              color: 'var(--palette-light-text-primary-value-hex)',
              textTransform: 'none',
              padding: 0,
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-tertiary-value-hex)',
              },
            }}
          >
            6
          </Button>
          <Button
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '6px',
              padding: '0px 24px',
              height: '56px',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 400,
              color: 'var(--palette-light-text-primary-value-hex)',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-tertiary-value-hex)',
              },
            }}
          >
            Next
            <Box
              component="svg"
              sx={{ width: '16px', height: '16px', ml: 0.5 }}
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M10.86 17.43L15.29 13L10.86 8.57L12.27 7.16L18.11 13L12.27 18.84L10.86 17.43Z"
                fill="var(--palette-light-text-primary-value-hex)"
              />
            </Box>
          </Button>
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 'var(--palette-light-font-size-small-value)', md: 'var(--palette-light-font-size-default-value)' },
            fontWeight: 400,
            lineHeight: '1.4',
            color: 'var(--palette-light-text-secondary-value-hex)',
          }}
        >
          1–10 of 52 orders
        </Typography>
      </Box>
    </Box>
  );
};

export default VendorTransactions;

